/**
 * Selection multiple dans la liste (« Select » de Bitwarden, multi-selection de
 * 1Password) : cocher plusieurs entrees, puis les mettre en favori, les
 * deplacer, les etiqueter (`entry-tags.ts`), les archiver, les jeter, les restaurer ou les purger en un geste.
 *
 * Trois regles tiennent la fonctionnalite :
 * - on n'agit QUE sur ce qui est coche ET visible (`bulkTargets`) : une entree
 *   cochee puis sortie de la vue par une recherche ou un tri n'est pas touchee
 *   a l'aveugle ;
 * - chaque vue n'offre que ses gestes (`bulkActionsFor`) : la corbeille
 *   restaure ou purge, elle n'archive pas ;
 * - une entree sensible (`master-password-reprompt.ts`) dans la selection
 *   exige le mot de passe maitre, comme sa fiche (`bulkNeedsReprompt`) : la
 *   selection multiple n'est pas un contournement de la re-demande.
 *
 * Module pur, zero E/S ; les ecritures passent par `VaultStore.applyBulk`.
 */
import type { VaultItem } from '@klavox/vault-model';
import { isArchived } from './entry-archive.js';
import { addTag, normalizeTag, removeTag, tagsChanged } from './entry-tags.js';
import { isTrashed } from './entry-trash.js';
import { isRepromptProtected } from './master-password-reprompt.js';

export type BulkView = 'vault' | 'archives' | 'trash';

export type BulkAction = 'favorite' | 'unfavorite' | 'move' | 'tag' | 'untag' | 'archive' | 'unarchive' | 'trash' | 'restore' | 'purge';

const ACTIONS_BY_VIEW: Readonly<Record<BulkView, readonly BulkAction[]>> = {
  vault: ['favorite', 'unfavorite', 'move', 'tag', 'untag', 'archive', 'trash'],
  archives: ['unarchive', 'move', 'tag', 'untag', 'trash'],
  trash: ['restore', 'purge'],
};

const LABELS: Readonly<Record<BulkAction, string>> = {
  favorite: 'Ajouter aux favoris',
  unfavorite: 'Retirer des favoris',
  move: 'Deplacer',
  tag: 'Etiqueter',
  untag: 'Retirer l’etiquette',
  archive: 'Archiver',
  unarchive: 'Desarchiver',
  trash: 'Supprimer',
  restore: 'Restaurer',
  purge: 'Supprimer definitivement',
};

/** Gestes proposes dans une vue, dans l'ordre d'affichage. */
export function bulkActionsFor(view: BulkView): readonly BulkAction[] {
  return ACTIONS_BY_VIEW[view];
}

/** Valeur venue du DOM : seuls les gestes connus passent (`constructor`, `__proto__`... non). */
export function isBulkAction(value: unknown): value is BulkAction {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(LABELS, value);
}

export function bulkActionLabel(action: BulkAction): string {
  return LABELS[action];
}

/**
 * L'entree est-elle concernee ? Une entree deja dans l'etat vise n'est pas
 * reecrite (pas de revision inutile a synchroniser) ; une entree jetee n'est
 * touchee que par « Restaurer » et « Supprimer definitivement ». `folderId` ne
 * sert qu'a « Deplacer », `tag` qu'a « Etiqueter » et « Retirer l'etiquette ».
 */
export function bulkApplies(item: VaultItem, action: BulkAction, folderId: string | null = null, tag = ''): boolean {
  if (item.deletedAt !== null) return false;
  const trashed = isTrashed(item.secrets);
  if (action === 'restore' || action === 'purge') return trashed;
  if (trashed) return false;
  switch (action) {
    case 'favorite':
      return !item.favorite;
    case 'unfavorite':
      return item.favorite;
    case 'move':
      return item.folderId !== folderId;
    case 'tag':
      return tagsChanged(item.secrets, addTag(item.secrets, tag));
    case 'untag':
      return tagsChanged(item.secrets, removeTag(item.secrets, tag));
    case 'archive':
      return !isArchived(item.secrets);
    case 'unarchive':
      return isArchived(item.secrets);
    case 'trash':
      return true;
  }
}

/** Ce sur quoi on agit : coche ET visible, dans l'ordre de la liste. */
export function bulkTargets(visible: readonly VaultItem[], selected: readonly string[]): VaultItem[] {
  const wanted = new Set(selected);
  return visible.filter((item) => wanted.has(item.id));
}

/** Une entree sensible dans la selection exige le mot de passe maitre, quel que soit le geste. */
export function bulkNeedsReprompt(targets: readonly VaultItem[]): boolean {
  return targets.some((item) => isRepromptProtected(item.secrets));
}

export function toggleBulkSelection(selected: readonly string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((candidate) => candidate !== id) : [...selected, id];
}

/**
 * Maj+clic (Gmail, Finder, Explorateur) : toute la plage de la liste AFFICHEE entre l'ancre (derniere ligne
 * cliquee) et la ligne cliquee prend l'etat que le clic donne a celle-ci -- cochee si elle ne l'etait pas,
 * decochee sinon. Une entree masquee par la recherche n'entre jamais dans la plage, et ce qui est coche hors
 * de la plage ne bouge pas. Sans ancre affichee (premier clic, ancre filtree par une recherche) : simple bascule.
 */
export function bulkRangeSelection(visible: readonly string[], selected: readonly string[], anchor: string | null, id: string): string[] {
  const to = visible.indexOf(id);
  const from = anchor === null ? -1 : visible.indexOf(anchor);
  if (to < 0 || from < 0) return toggleBulkSelection(selected, id);
  const range = new Set(visible.slice(Math.min(from, to), Math.max(from, to) + 1));
  if (selected.includes(id)) return selected.filter((candidate) => !range.has(candidate));
  return [...selected, ...[...range].filter((candidate) => !selected.includes(candidate))];
}

/**
 * Maj+fleche (Finder, Explorateur, Gmail) : la ligne atteinte et toute la plage AFFICHEE depuis l'ancre prennent
 * l'etat de l'ancre -- cochee si l'ancre l'est, decochee sinon ; l'ancre ne bouge pas, si bien que Maj+bas puis
 * Maj+haut ne fait que reparcourir la meme plage. Ce qui est coche hors de la plage, masque compris, ne bouge pas.
 * Sans ancre affichee : la ligne atteinte est cochee (elle devient l'ancre, cote `app.ts`).
 */
export function bulkExtendSelection(visible: readonly string[], selected: readonly string[], anchor: string | null, id: string): string[] {
  const to = visible.indexOf(id);
  const from = anchor === null ? -1 : visible.indexOf(anchor);
  if (to < 0) return [...selected];
  if (from < 0) return selected.includes(id) ? [...selected] : [...selected, id];
  const range = new Set(visible.slice(Math.min(from, to), Math.max(from, to) + 1));
  if (!selected.includes(anchor!)) return selected.filter((candidate) => !range.has(candidate));
  return [...selected, ...[...range].filter((candidate) => !selected.includes(candidate))];
}

/**
 * Tout cocher (bouton « Tout cocher », Ctrl+A / Cmd+A) : toute la liste AFFICHEE rejoint la selection ; ce qui est
 * deja coche reste, masque compris -- une recherche qui masque une entree ne la decoche pas.
 */
export function bulkSelectAll(visible: readonly string[], selected: readonly string[]): string[] {
  return [...new Set([...selected, ...visible])];
}

/** Ce qu'une touche fait sur une ligne cochable ; `null` = la touche reste au navigateur (Espace seul coche deja). */
export type BulkRowKey = 'range' | 'all' | BulkFocusMove | 'extend-next' | 'extend-previous' | 'extend-first' | 'extend-last';

/** Ou une touche de deplacement envoie le focus : ligne voisine, premiere ou derniere ligne affichee. */
export type BulkFocusMove = 'next' | 'previous' | 'first' | 'last';

/**
 * La ligne qu'atteint une touche de deplacement depuis `from`, dans la liste AFFICHEE (une entree masquee par la
 * recherche n'est jamais atteinte). `null` : ligne de depart absente, ou pas de voisine -- la touche reste au navigateur.
 */
export function bulkFocusTarget(visible: readonly string[], from: string, move: BulkFocusMove): string | null {
  const index = visible.indexOf(from);
  if (index < 0) return null;
  if (move === 'first') return visible[0]!;
  if (move === 'last') return visible[visible.length - 1]!;
  const next = index + (move === 'next' ? 1 : -1);
  return next < 0 || next >= visible.length ? null : visible[next]!;
}

/**
 * Le clavier de la selection multiple, au meme niveau que la souris (Finder, Explorateur, Gmail) : fleches bas / haut
 * d'une ligne a l'autre, Origine / Fin a la premiere / derniere ligne affichee, Maj+fleche / Maj+Origine / Maj+Fin
 * etendent la plage (`bulkExtendSelection`), Maj+Espace ou Maj+Entree fait ce que fait Maj+clic
 * (`bulkRangeSelection`) -- lu a l'appui, car le clic que le navigateur deduit d'une touche ne porte pas Maj de
 * facon fiable -- et Ctrl+A / Cmd+A coche tout l'affiche (`bulkSelectAll`). Hors Ctrl+A / Cmd+A seul : jamais avec
 * Ctrl, Cmd ou Alt (raccourcis du systeme), jamais pendant une composition.
 */
export function bulkRowKey(event: { readonly key: string; readonly shiftKey: boolean; readonly ctrlKey: boolean; readonly metaKey: boolean; readonly altKey: boolean; readonly isComposing?: boolean }): BulkRowKey | null {
  // Ctrl+A ou Cmd+A, l'un sans l'autre, sans Maj ni Alt : le seul raccourci a modificateur pris (« a » ou « A », verrou majuscule).
  if (event.isComposing !== true && event.ctrlKey !== event.metaKey && !event.shiftKey && !event.altKey && event.key.toLowerCase() === 'a') return 'all';
  if (event.isComposing === true || event.ctrlKey || event.metaKey || event.altKey) return null;
  if (event.key === 'ArrowDown') return event.shiftKey ? 'extend-next' : 'next';
  if (event.key === 'ArrowUp') return event.shiftKey ? 'extend-previous' : 'previous';
  if (event.key === 'Home') return event.shiftKey ? 'extend-first' : 'first';
  if (event.key === 'End') return event.shiftKey ? 'extend-last' : 'last';
  if (event.shiftKey && (event.key === ' ' || event.key === 'Enter')) return 'range';
  return null;
}

/** Message de fin : le nombre d'entrees reellement modifiees, pas celui des cases cochees. */
export function bulkDoneMessage(action: BulkAction, count: number, trashRetentionDays: number, tag = ''): string {
  const n = `${count} entree(s)`;
  const label = normalizeTag(tag);
  switch (action) {
    case 'favorite':
      return `${n} ajoutee(s) aux favoris`;
    case 'unfavorite':
      return `${n} retiree(s) des favoris`;
    case 'move':
      return `${n} deplacee(s)`;
    case 'tag':
      return `${n} etiquetee(s) « ${label} »`;
    case 'untag':
      return `Etiquette « ${label} » retiree de ${n}`;
    case 'archive':
      return `${n} archivee(s) -- retrouvez-les dans Archives`;
    case 'unarchive':
      return `${n} desarchivee(s) -- de retour dans le coffre`;
    case 'trash':
      return `${n} placee(s) dans la corbeille -- restaurables pendant ${trashRetentionDays} jours`;
    case 'restore':
      return `${n} restauree(s)`;
    case 'purge':
      return `${n} supprimee(s) definitivement`;
  }
}
